 
import {
  faCheck,
  faCheckCircle,
  faEllipsisV,
  faQuestion,
  faRedo,
  faTimes,
  faUserGroupSimple,
} from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import MenuItem from '@mui/material/MenuItem';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Badge } from 'src/components/Badge';
import { Button } from 'src/components/Button';
import { DropdownMenu, useDropdownMenu } from 'src/components/DropdownMenu';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Tooltip } from 'src/components/Tooltip';
import { Typography } from 'src/components/Typography';
import { Appointment } from 'src/models/appointment/_types';
import { getUserDisplayName } from 'src/models/user/utils';
import { Visit, VisitStatus } from 'src/models/visit/_types';
import {
  getVisitChaperone,
  getVisitStatus,
  isGuest,
} from 'src/models/visit/utils';
import { FirstVisit } from 'src/shared/FirstVisit';
import { PermissionCheck } from 'src/shared/PermissionCheck';
import { VisitStatusDropdownItems } from 'src/shared/VisitStatus/VisitStatusDropdownItems';
import toast from 'src/utils/toast';

import { AppointmentCardSection } from '../AppointmentCardSection';

import { useAppointmentMembersSection } from './useAppointmentMembersSection';


// You'll need to send the onclick back to parent so we can set visit if trying to view the user
// onClick={() => setVisit(appointment.visits[0])}

export const AppointmentMembersSection = ({
  appointment,
  showWaitlist = false,
  setVisit,
}) => {
  const {
    updateVisitStatus,
    bulkUpdateVisits,
    deleteWaitlistEntry,
    isUpdatingVisit,
  } = useAppointmentMembersSection();
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentMembersSection',
  });

  const BulkStatusMenuItem = ({ newStatus, text }) => (
    <MenuItem
      onClick={() => {
        bulkUpdateVisits(appointment.visits, {
          status: newStatus,
        });
      }}
    >
      <Typography>{text}</Typography>
    </MenuItem>
  );

  if (!showWaitlist && !appointment.visits.length) {
    return (
      <AppointmentCardSection>
        <Typography variant="body2" color="text.secondary">
          {t('noAppointmentMembers')}
        </Typography>
      </AppointmentCardSection>
    );
  }

  if (showWaitlist && !appointment.entries.length) {
    return (
      <AppointmentCardSection>
        <Typography variant="body2" color="text.secondary">
          {t('noWaitlistMembers')}
        </Typography>
      </AppointmentCardSection>
    );
  }

  const completedCount = appointment.visits.filter(
    (v: Visit) => v.status == 'completed'
  ).length;
  const totalCount = appointment.visits.filter((v: Visit) =>
    ['reserved', 'registered', 'completed'].includes(v.status)
  ).length;

  return (
    <PermissionCheck can="viewAppointmentMembers" object={appointment}>
      {!showWaitlist && (
        <AppointmentCardSection
          disableChildPadding
          title={t('checkedInCount', {
            completed: completedCount,
            total: totalCount,
          })}
          actions={
            <PermissionCheck can="editAppointmentMembers" object={appointment}>
              <DropdownMenu
                trigger={<Button color="primary">{t('manage')}</Button>}
              >
                <BulkStatusMenuItem
                  newStatus={VisitStatus.Registered}
                  text={t('confirmEveryone')}
                />
                <BulkStatusMenuItem
                  newStatus={VisitStatus.Completed}
                  text={t('completeEveryone')}
                />
                <BulkStatusMenuItem
                  newStatus={VisitStatus.Cancelled}
                  text={t('earlyCancelEveryone')}
                />
                <BulkStatusMenuItem
                  newStatus={VisitStatus.LateCancelled}
                  text={t('lateCancelEveryone')}
                />
                <BulkStatusMenuItem
                  newStatus={VisitStatus.Reset}
                  text={t('resetEveryone')}
                />
              </DropdownMenu>
            </PermissionCheck>
          }
        >
          {appointment.visits.map((visit) => (
            <VisitListItem
              key={visit.id}
              visit={visit}
              appointment={appointment}
              onClick={() => !isGuest(visit) && setVisit(visit)}
              onUpdateStatus={updateVisitStatus}
              isUpdatingVisit={isUpdatingVisit}
            />
          ))}
        </AppointmentCardSection>
      )}
      {showWaitlist && (
        <AppointmentCardSection
          disableChildPadding
          title={t('waitingCount', { count: appointment.entries.length })}
        >
          {appointment.entries.map((entry) => {
            return (
              <WaitlistListItem
                key={entry.id}
                entry={entry}
                appointment={appointment}
                onDelete={() => {
                  deleteWaitlistEntry(entry);
                }}
              />
            );
          })}
        </AppointmentCardSection>
      )}
    </PermissionCheck>
  );
};

interface VisitListItemProps {
  visit: Visit;
  appointment: Appointment;
  onClick?: () => void;
  onUpdateStatus?: (
    visit: Visit,
    status: VisitStatus,
    options?: {
      chargeNoshow?: boolean;
      chargeCancellation?: boolean;
      paidByPackageId?: boolean;
    }
  ) => void;
  showStatus?: boolean;
  isUpdatingVisit?: boolean;
}

export const VisitListItem = ({
  visit,
  appointment,
  onClick = undefined,
  onUpdateStatus = undefined,
  showStatus = true,
  isUpdatingVisit = false,
}: VisitListItemProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentMembersSection',
  });

  const VisitBadge = ({ visit }) => {
    const color = visit.status === 'completed' ? 'success' : 'error';
    const hasIcon = [
      VisitStatus.Completed,
      VisitStatus.Cancelled,
      VisitStatus.LateCancelled,
      VisitStatus.NoShowed,
    ].includes(visit.status);
    const visitIcon =
      visit.status === VisitStatus.Completed
        ? faCheck
        : visit.status == VisitStatus.NoShowed
        ? faQuestion
        : faTimes;

    return (
      <Badge
        overlap="circular"
        color={color}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        icon={hasIcon && showStatus ? visitIcon : undefined}
      >
        {isGuest(visit) ? (
          <GuestAvatar />
        ) : (
          <Avatar src={visit.user?.profileImage} />
        )}
      </Badge>
    );
  };

  const VisitQuickCheckinButton = ({ visit }) => {
    const completed = visit.status == VisitStatus.Completed;
    return (
      <>
        {!completed && onUpdateStatus && (
          <IconButton
            onClick={() => {
              onUpdateStatus(visit, VisitStatus.Completed);
            }}
          >
            <Icon
              icon={faCheckCircle}
            />
          </IconButton>
        )}
      </>
    );
  };

  const secondaryText = isGuest(visit)
    ? t('guestFor', {
        name: getUserDisplayName(getVisitChaperone(visit, appointment)),
      })
    : getVisitStatus(visit);

  const dropdownMenu = useDropdownMenu();

  return (
    <ListItem
      key={visit.id}
      onClick={onClick}
      avatar={<VisitBadge visit={visit} />}
      primary={
        visit.firstVisit ? (
          <>
            <Stack direction="row" spacing={0.5}>
              {visit.userName}
              <FirstVisit />
            </Stack>
          </>
        ) : (
          visit.userName
        )
      }
      secondary={showStatus ? secondaryText : undefined}
      rightAdornment={
        <PermissionCheck can="editAppointmentMembers" object={appointment}>
          <Stack
            direction="row"
            spacing={1}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <VisitQuickCheckinButton visit={visit} />
            {!visit.paidByPackageId && onUpdateStatus && (
              <Tooltip title="Attempt Package Deduction">
                <IconButton
                  onClick={() => {
                    onUpdateStatus(visit, visit.status, {
                      paidByPackageId: true,
                    });
                    toast.success('Package deduction attempted');
                  }}
                  disabled={isUpdatingVisit}
                >
                  <Icon
                    icon={faRedo}
                  />
                </IconButton>
              </Tooltip>
            )}
            <DropdownMenu
              closeOnContentClick={false}
              open={dropdownMenu.isOpen}
              setOpen={dropdownMenu.setIsOpen}
              trigger={
                <IconButton>
                  <Icon
                    icon={faEllipsisV}
                  />
                </IconButton>
              }
            >
              {onUpdateStatus && (
                <VisitStatusDropdownItems
                  visit={visit}
                  appointment={appointment}
                  setIsOpen={dropdownMenu.setIsOpen}
                  updateVisitStatus={onUpdateStatus}
                />
              )}
            </DropdownMenu>
          </Stack>
        </PermissionCheck>
      }
    />
  );
};

const WaitlistListItem = ({ entry, appointment, onDelete }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentMembersSection',
  });
  const name = [entry.firstName, entry.lastName].filter((e) => e).join(' ');
  const details = [entry.email, entry.phone_number]
    .filter((e) => e)
    .join(' | ');

  return (
    <ListItem
      key={entry.id}
      avatar={<Avatar src={entry.user?.profileImage} />}
      primary={name}
      secondary={details}
      rightAdornment={
        <PermissionCheck can="editAppointmentMembers" object={appointment}>
          <Stack direction="row" spacing={1}>
            <DropdownMenu
              trigger={
                <IconButton>
                  <Icon
                    icon={faEllipsisV}
                  />
                </IconButton>
              }
            >
              <MenuItem component={Link} onClick={onDelete}>
                {t('deleteEntry')}
              </MenuItem>
            </DropdownMenu>
          </Stack>
        </PermissionCheck>
      }
    />
  );
};

const GuestAvatar = () => (
  <Avatar>
    <Icon
      icon={faUserGroupSimple}
    />
  </Avatar>
);
